import { router } from 'expo-router';
import {
    H6Regular,
    isNonMobileWeb,
    PillButton,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import ErrorWidget from '../../common/components/ErrorWidget';
import type { SearchResult } from '../../http/Fetcher';
import { captureException } from '../../logger/Logger';
import { getMinutesInProgress, sessionIsInProgress } from '../helpers';
import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import type { GuidanceSession } from '../types/GuidanceSessions';
import { UpcomingSessionCardContext } from '../types/GuidanceSessions';
import BookSessionCard from './BookSessionCard';
import UpcomingSessionCard from './UpcomingSessionCard';

interface SessionsWidgetProps {
    isLoading: boolean;
    upcomingSessions: SearchResult<GuidanceSession> | undefined;
    pastSessions: SearchResult<GuidanceSession> | undefined;
    hasCompletedGuidanceOnboarding: boolean;
}

const SessionsWidget = ({
    isLoading,
    upcomingSessions,
    pastSessions,
    hasCompletedGuidanceOnboarding,
}: SessionsWidgetProps) => {
    const { getNextGuidanceOnboardingSessionType } =
        useMembershipGuidanceSessionInfo();
    const upcomingSession = getNextUpcomingSession(
        upcomingSessions?.results ?? [],
    );
    const inProgressSession =
        upcomingSession && sessionIsInProgress(upcomingSession)
            ? upcomingSession
            : null;
    const isInProgressSessionDecentlyFarIn =
        getMinutesInProgress(inProgressSession) > 15;

    const canBookSession =
        !hasCompletedGuidanceOnboarding &&
        (upcomingSession == null || isInProgressSessionDecentlyFarIn);
    const showBookSessionButton = canBookSession;

    const nextGuidanceSessionType = getNextGuidanceOnboardingSessionType([
        ...(pastSessions?.results ?? []),
        ...(inProgressSession ? [inProgressSession] : []), // include current session in case member wants to book next session during the current session
    ]);
    // This should never happen, but still check for it.
    if (!isLoading && upcomingSessions == null && !canBookSession) {
        captureException(
            new Error(`Sessions widget mounted with no data to display.`),
        );
        return <ErrorWidget />;
    }

    return (
        <YStack>
            <XStack
                justifyContent="space-between"
                alignItems="flex-start"
                minHeight={40}
            >
                <H6Regular>Sessions</H6Regular>
                {showBookSessionButton && !isNonMobileWeb && (
                    <XStack marginTop={-4}>
                        <BookSessionButton />
                    </XStack>
                )}
            </XStack>

            {isLoading ? (
                <Spinner marginVertical={170} />
            ) : (
                <>
                    {upcomingSession && !isInProgressSessionDecentlyFarIn && (
                        <UpcomingSessionCard
                            session={upcomingSession}
                            context={UpcomingSessionCardContext.MY_GUIDE}
                        />
                    )}

                    {canBookSession && (
                        <BookSessionCard
                            sessionType={nextGuidanceSessionType}
                            hideButton={showBookSessionButton}
                        />
                    )}
                    {showBookSessionButton && isNonMobileWeb && (
                        <XStack position="absolute" bottom={15} right={15}>
                            <BookSessionButton />
                        </XStack>
                    )}
                </>
            )}
        </YStack>
    );
};

const BookSessionButton = () => {
    return (
        <PillButton
            text="Book a session"
            onPress={() => {
                router.navigate('/guidance-sessions/select-session');
            }}
        />
    );
};

const getNextUpcomingSession = (
    sessions: GuidanceSession[],
): GuidanceSession | null => {
    const sortedSessions = sessions.sort((a, b) => {
        return (
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );
    });

    return sortedSessions[0] ?? null;
};

export default SessionsWidget;
